import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { t } from '@/i18n';

export const FlowLegend = ({ nodes = [], edges = [] }) => {
  const [open, setOpen] = useState(false);

  const nodeCounts = useMemo(() => ({
    ok: nodes.filter(n => n.status === 'ok').length,
    warning: nodes.filter(n => n.status === 'warning').length,
    error: nodes.filter(n => n.status === 'error').length,
  }), [nodes]);

  const edgeCounts = useMemo(() => ({
    active: edges.filter(e => e.status === 'active').length,
    reduced: edges.filter(e => e.status === 'reduced').length,
    stopped: edges.filter(e => e.status === 'stopped' || !e.status).length,
  }), [edges]);

  return (
    <div className="absolute bottom-4 left-14 z-10 bg-white/85 backdrop-blur-md border border-border rounded-xl shadow-elevated overflow-hidden w-[170px]">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between p-3 text-[10px] font-semibold text-muted-foreground hover:bg-white/50 transition-colors">
          <span>{t('supplyChain.legend')}</span>
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 px-3 pb-3 pt-0">
            <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide">{t('supplyChain.legend.nodeStatus')}</div>
            {[
              { color: 'bg-green-500', label: t('supplyChain.legend.operational'), count: nodeCounts.ok },
              { color: 'bg-yellow-500', label: t('supplyChain.legend.warning'), count: nodeCounts.warning },
              { color: 'bg-red-500', label: t('supplyChain.legend.errorBottleneck'), count: nodeCounts.error },
            ].map(item => (
              <div key={item.label} className={cn('flex items-center gap-2 text-[10px]', item.count === 0 && 'opacity-40')}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${item.color}`} />
                <span className="truncate">{item.label}</span>
                <span className="ml-auto text-muted-foreground font-medium shrink-0" style={{ fontVariantNumeric: 'tabular-nums' }}>({item.count})</span>
              </div>
            ))}

            <div className="border-t border-border/50 pt-2 mt-2">
              <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('supplyChain.legend.flowStatus')}</div>
              {[
                { color: '#3b82f6', label: t('supplyChain.legend.activeFlow'), count: edgeCounts.active },
                { color: '#eab308', label: t('supplyChain.legend.reducedFlow'), count: edgeCounts.reduced },
                { color: '#9ca3af', label: t('supplyChain.legend.stopped'), count: edgeCounts.stopped },
              ].map(item => (
                <div key={item.label} className={cn('flex items-center gap-2 text-[10px]', item.count === 0 && 'opacity-40')}>
                  <div className="w-4 h-0.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="truncate">{item.label}</span>
                  <span className="ml-auto text-muted-foreground font-medium shrink-0" style={{ fontVariantNumeric: 'tabular-nums' }}>({item.count})</span>
                </div>
              ))}
            </div>

            <div className="border-t border-border/50 pt-2 mt-2">
              <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('supplyChain.legend.capacity')}</div>
              {[
                { color: 'from-green-400 to-green-500', label: t('supplyChain.legend.capacityUnder60') },
                { color: 'from-yellow-400 to-yellow-500', label: t('supplyChain.legend.capacity60to85') },
                { color: 'from-red-400 to-red-500', label: t('supplyChain.legend.capacityOver85') },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2 text-[10px]">
                  <div className={`w-4 h-2 rounded-full shrink-0 bg-gradient-to-r ${item.color}`} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
